/**
 * Care Page - CCM (Connected Community Model) Resource Hub
 *
 * Displays curated resources across four categories:
 * - Capacity: Team, skills, and operational resources
 * - Care: Wellness and personal development
 * - Capital: Funding and financial resources
 * - Community Model: Networks and partnerships
 *
 * Each section contains tiles that can be clicked to view Player Cards
 *
 * Resources are fetched from Firestore (entities/{entityId}/care subcollection)
 * with fallback to demo data when no Firestore resources exist.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useEntityContext } from '../EntityContextSwitch';
import CCMSection from './CCMSection';
import PlayerCardModal from './PlayerCardModal';
import AdminControls from './AdminControls';
import { SECTION_CONFIG, PLAYER_CARDS } from './demoResources';
import useCareResources from '../../hooks/useCareResources';

// eslint-disable-next-line max-lines-per-function
function Care() {
  const { currentEntity, loading: entityLoading } = useEntityContext();

  // Fetch Care resources from Firestore (with demo fallback)
  const {
    resources,
    loading: resourcesLoading,
    error: resourcesError,
    isUsingDemoData,
    removeResource,
    moveResource,
  } = useCareResources(currentEntity?.id, true);

  // State for player card modal
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // State for admin mode
  const [adminMode, setAdminMode] = useState(false);
  const [showAdminControls, setShowAdminControls] = useState(false);

  // Handle tile click - open player card modal
  const handleTileClick = (entity) => {
    setSelectedEntity(entity);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEntity(null);
  };

  // Handle opening full player card page
  const handleOpenFullCard = () => {
    if (selectedEntity) {
      window.open(`/care/player-card/${selectedEntity.id}`, '_blank');
    }
  };

  // Get player card data for selected entity
  const getPlayerCardData = (entityId) => {
    return PLAYER_CARDS[entityId] || {
      ...PLAYER_CARDS['default'],
      snapshot: {
        ...PLAYER_CARDS['default'].snapshot,
        name: selectedEntity?.name || '',
        title: selectedEntity?.title || '',
        organization: selectedEntity?.organization || '',
      },
      entityType: selectedEntity?.entityType || 'person',
    };
  };

  // Admin functions
  const handleRemoveTile = async (sectionId, resourceId) => {
    if (!isUsingDemoData) {
      try {
        await removeResource(resourceId);
      } catch (error) {
        console.error('Failed to remove resource:', error);
      }
    }
    // For demo data, we don't persist removals
  };

  const handleMoveTile = (sectionId, resourceId, direction) => {
    moveResource(sectionId, resourceId, direction);
  };

  // Loading state
  if (entityLoading || resourcesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (resourcesError && !isUsingDemoData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load Care resources: {resourcesError}
        </Alert>
      </Box>
    );
  }

  // No entity selected
  if (!currentEntity?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please select an entity from the header to view their curated CCM resources.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 8 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 4,
      }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Connected Community
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Curated resources for <strong>{currentEntity.name}</strong> across capacity, care, capital, and community.
          </Typography>
        </Box>

        {/* Admin toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={adminMode}
                onChange={(e) => setAdminMode(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Coach Mode
              </Typography>
            }
          />
          {adminMode && (
            <Tooltip title="Manage CCM Resources">
              <IconButton
                onClick={() => setShowAdminControls(true)}
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* CCM Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(SECTION_CONFIG).map(([key, config]) => (
          <CCMSection
            key={key}
            sectionId={key}
            config={config}
            resources={resources[key] || []}
            onTileClick={handleTileClick}
            adminMode={adminMode}
            onRemoveTile={handleRemoveTile}
            onMoveTile={handleMoveTile}
          />
        ))}
      </Box>

      {/* Player Card Modal */}
      <PlayerCardModal
        open={modalOpen}
        onClose={handleModalClose}
        entity={selectedEntity}
        playerCardData={selectedEntity ? getPlayerCardData(selectedEntity.id) : null}
        onOpenFull={handleOpenFullCard}
      />

      {/* Admin Controls Drawer */}
      <AdminControls
        open={showAdminControls}
        onClose={() => setShowAdminControls(false)}
        resources={resources}
        sectionConfig={SECTION_CONFIG}
        onAddResource={() => {/* TODO: implement add resource */}}
      />
    </Box>
  );
}

export default Care;
